/** The front door. Keep this page independent of the game's 3D boot. */
import { MEADOW_ENTRY } from "./entry-points.js";
import { deckle, injectHudStyle, runeMask } from "./render/hud/frame.js";
import { hueColor } from "./host/vfx/rune-palette.js";
import { buildStarSvg, setStarBuild } from "./render/wayfarer-star.js";
import { bootSave, cloudSave, saveStore } from "./save/index.js";
import { mountSavePanel } from "./save/panel.js";
import type { RuneHue } from "./types.js";
import "./boot.css";

interface MenuEntry {
  readonly href: string;
  readonly title: string;
  readonly blurb: string;
  readonly rune: RuneHue;
  readonly group: "world" | "tool";
}

/** Reading order is also keyboard order. The meadow remains the live game. */
export const MENU_ENTRIES: readonly MenuEntry[] = [
  {
    href: MEADOW_ENTRY.href,
    title: "Veláris",
    blurb: "The village & the meadows",
    rune: "vill",
    group: "world",
  },
  {
    href: "/ways.html",
    title: "The Ways",
    blurb: "The paths between worlds",
    rune: "sol",
    group: "world",
  },
  {
    href: "/arena.html",
    title: "Arena",
    blurb: "A place to test your craft",
    rune: "vef",
    group: "world",
  },
  {
    href: "/sandbox-terrain.html",
    title: "Terrain editor",
    blurb: "Sculpt, paint and shape the land.",
    rune: "man",
    group: "tool",
  },
  {
    href: "/sandbox.html",
    title: "Character sandbox",
    blurb: "Explore characters, clothing and movement.",
    rune: "skugg",
    group: "tool",
  },
];

function menuLink(entry: MenuEntry): HTMLAnchorElement {
  const link = document.createElement("a");
  link.href = entry.href;
  link.className = `entry-link entry-link--${entry.group}`;
  link.dataset["menuEntry"] = entry.title;
  link.style.setProperty("--rune-color", hueColor(entry.rune));

  const rune = document.createElement("span");
  rune.className = "entry-rune";
  rune.setAttribute("aria-hidden", "true");
  runeMask(rune, entry.rune);

  const text = document.createElement("span");
  text.className = "entry-link-copy";
  const title = document.createElement("span");
  title.className = "entry-link-title";
  title.textContent = entry.title;
  text.append(title);

  if (entry.group === "world") {
    const blurb = document.createElement("span");
    blurb.className = "entry-link-blurb";
    blurb.textContent = entry.blurb;
    text.append(blurb);
    const arrow = document.createElement("span");
    arrow.className = "entry-link-arrow";
    arrow.textContent = "↗";
    arrow.setAttribute("aria-hidden", "true");
    link.append(rune, text, arrow);
  } else {
    link.append(rune, text);
  }
  return link;
}

export function renderMenu(host: HTMLElement, entries: readonly MenuEntry[]): HTMLElement {
  injectHudStyle();
  const menu = document.createElement("main");
  menu.id = "menu";
  menu.className = "entry-menu";
  menu.setAttribute("aria-labelledby", "entry-title");
  deckle(menu);

  const art = document.createElement("figure");
  art.className = "entry-art";
  deckle(art, 10, { seam: false });
  const picture = document.createElement("img");
  picture.className = "entry-art-image";
  picture.src = "/ui/entry/runa-crimson-velaris.webp";
  picture.alt =
    "Rúna at the edge of a crimson meadow, overlooking Veláris in the golden evening light.";
  picture.width = 1254;
  picture.height = 1254;
  picture.fetchPriority = "high";
  art.append(picture);

  const panel = document.createElement("section");
  panel.className = "entry-panel";
  panel.setAttribute("aria-label", "Choose your path");
  const header = document.createElement("header");
  header.className = "entry-heading";
  const star = buildStarSvg();
  setStarBuild(star, { ring: 1, spikes: 1, runes: 1, reversing: false });
  star.svg.classList.add("entry-sigil");
  star.svg.setAttribute("aria-hidden", "true");
  const title = document.createElement("h1");
  title.id = "entry-title";
  title.textContent = "Weftbound";
  header.append(star.svg, title);

  const worlds = document.createElement("nav");
  worlds.className = "entry-worlds";
  worlds.setAttribute("aria-label", "Enter the world");
  const tools = document.createElement("nav");
  tools.className = "entry-tools";
  tools.setAttribute("aria-label", "Creative tools");
  for (const entry of entries) {
    (entry.group === "world" ? worlds : tools).append(menuLink(entry));
  }

  panel.append(header, worlds, tools);
  menu.append(art, panel);
  host.append(menu);
  return menu;
}

export function boot(): void {
  const menu = renderMenu(document.body, MENU_ENTRIES);
  // ADR-0070: the save lives behind the front door — status, key, Reset.
  const panel = menu.querySelector<HTMLElement>(".entry-panel");
  if (panel !== null) mountSavePanel(panel, { store: saveStore, cloud: cloudSave });
  void bootSave();
  console.log("[boot] menu");
  mountBackdropWhenPainted();
}

/**
 * The live night behind the menu (`render/menu-backdrop.ts`): loom-flies over
 * Velaris. A dynamic import AFTER the menu's first paint, so the menu chunk
 * carries none of three and the menu shows at once; `?nobackdrop` skips it,
 * and without WebGPU the CSS darkness simply stays.
 */
function mountBackdropWhenPainted(): void {
  if (location.search.includes("nobackdrop")) return;
  const layer = document.createElement("div");
  layer.className = "entry-backdrop";
  layer.setAttribute("aria-hidden", "true");
  document.body.prepend(layer);
  requestAnimationFrame(() => {
    setTimeout(() => {
      import("./render/menu-backdrop.js")
        .then(async (m) => m.mountMenuBackdrop(layer))
        .catch((err: unknown) => {
          console.info("[boot] menu backdrop skipped:", err);
        });
    }, 0);
  });
}
